import { cva, type VariantProps } from "class-variance-authority";
import { type FC, type HTMLAttributes, type ReactNode, type Ref } from "react";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";

const bannerVariants = cva(
	"relative w-full h-fit min-h-fit flex items-center gap-3 rounded-md border px-4 py-3 text-primary",
	{
		variants: {
			// Semantic intent — drives border + surface.
			// Absorbs the legacy `InfoCard`, `ActionCard`, and `NotificationCard` wrappers
			// as well as the error-state message blocks (e.g. GlobalErrorHandlingProvider).
			tone: {
				default: "border-bc-default bg-surface-raised",
				confirm: "border-intent-success-border bg-intent-success-bg",
				info: "border-intent-info-border bg-intent-info-bg",
				neutral: "border-intent-neutral-border bg-intent-neutral-bg",
				notification: "border-intent-warning-border bg-intent-warning-bg",
				danger: "border-intent-danger-border bg-intent-danger-bg",
			},
		},
		defaultVariants: { tone: "default" },
	},
);

export const BANNER_TONES = [
	"default",
	"confirm",
	"info",
	"neutral",
	"notification",
	"danger",
] as const;
export type BannerTone = (typeof BANNER_TONES)[number];

type RootProps = HTMLAttributes<HTMLDivElement> &
	VariantProps<typeof bannerVariants> & {
		/**
		 * Semantic intent of the banner. Drives border and surface colors.
		 * - `default`      — neutral bordered surface
		 * - `confirm`      — success surface, for success-state messages
		 * - `info`         — information surface (replaces `InfoCard`)
		 * - `neutral`      — muted gray surface (replaces `ActionCard`)
		 * - `notification` — warning surface (replaces `NotificationCard`)
		 * - `danger`       — danger surface, for error-state messages
		 * @default "default"
		 */
		tone?: VariantProps<typeof bannerVariants>["tone"];
		trackingId?: string;
		children?: ReactNode;
		ref?: Ref<HTMLDivElement>;
	};

const Root: FC<RootProps> = ({ tone, trackingId, className, children, ref, ...rest }) => (
	<div
		ref={ref}
		className={cn(bannerVariants({ tone }), className)}
		{...trackingAttrs(trackingId)}
		{...rest}
	>
		{children}
	</div>
);
Root.displayName = "Banner";

type SlotProps = HTMLAttributes<HTMLDivElement> & {
	trackingId?: string;
	ref?: Ref<HTMLDivElement>;
};

// Pinned to the start, never shrinks. Typically an `<Icon />`.
const Leading: FC<SlotProps> = ({ trackingId, className, ref, ...rest }) => (
	<div
		ref={ref}
		className={cn("shrink-0 flex items-center", className)}
		{...trackingAttrs(trackingId)}
		{...rest}
	/>
);
Leading.displayName = "Banner.Leading";

// The flexible message region. Fills the space between Leading and Trailing.
const Content: FC<SlotProps> = ({ trackingId, className, ref, ...rest }) => (
	<div
		ref={ref}
		className={cn("flex-1 min-w-0", className)}
		{...trackingAttrs(trackingId)}
		{...rest}
	/>
);
Content.displayName = "Banner.Content";

// Pinned to the end, never shrinks. Typically an action button or dismiss control.
const Trailing: FC<SlotProps> = ({ trackingId, className, ref, ...rest }) => (
	<div
		ref={ref}
		className={cn("shrink-0 flex items-center", className)}
		{...trackingAttrs(trackingId)}
		{...rest}
	/>
);
Trailing.displayName = "Banner.Trailing";

export const Banner = Object.assign(Root, { Leading, Content, Trailing });

export type BannerProps = RootProps;
export type BannerSlotProps = SlotProps;
